import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api'
import { useAsync } from '../lib/useAsync'
import { ErrorState, LoadingBlock, NoResults } from '../components/States'
import { plural } from '../lib/format'

/**
 * Every company using the product, and how many people are in each.
 *
 * Ours is not on this list. It holds the people who set companies up rather
 * than a customer, and showing it invites somebody to treat it as one.
 */
export function Companies() {
  const companies = useAsync(() => api.listCompanies(), [])
  const [query, setQuery] = useState('')

  if (companies.loading) return <LoadingBlock label="Loading companies" />
  if (companies.error) return <ErrorState message={companies.error} onRetry={companies.reload} />

  const all = companies.data ?? []
  // Name and short name. Somebody arrives here knowing one or the other, and
  // the short name is what appears in a login when they are reading a support
  // message.
  const q = query.trim().toLowerCase()
  const rows = q
    ? all.filter((c) => c.name.toLowerCase().includes(q) || c.slug.includes(q))
    : all

  return (
    <section className="screen">
      <header className="title-row">
        <div>
          <h1 className="page">Companies</h1>
          <p className="subline">{plural(all.length, 'company', 'companies')}</p>
        </div>
        <div className="actions">
          <Link className="btn btn--primary" to="/companies/new">Add a company</Link>
        </div>
      </header>

      {/* Only once there is something to search. An empty library with a
          search box over it is a box that can only ever find nothing. */}
      {all.length > 0 && (
        <div className="search-row">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search companies"
            aria-label="Search companies by name or short name"
          />
          <span className="count">
            {q ? `${rows.length} of ${all.length} match` : plural(all.length, 'company', 'companies')}
          </span>
        </div>
      )}

      {!all.length ? (
        <div className="state state--empty">
          <h2>No companies yet</h2>
          <p>Add the first one and its admin can sign in straight away.</p>
          <Link className="btn btn--primary" to="/companies/new">Add a company</Link>
        </div>
      ) : !rows.length ? (
        // Different from empty on purpose. "None yet" and "none matched" are
        // two different problems with two different next steps.
        <NoResults query={query} onClear={() => setQuery('')} />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Short name</th>
                <th>Sign-in domains</th>
                <th className="num">People</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.slug}>
                  <td><Link to={`/companies/${encodeURIComponent(c.slug)}`}>{c.name}</Link></td>
                  <td><code className="mono">{c.slug}</code></td>
                  <td>
                    {c.domains.length
                      ? c.domains.join(', ')
                      : <span className="subline">none yet</span>}
                  </td>
                  <td className="num">{c.people.toLocaleString()}</td>
                  <td>
                    {c.createdAt
                      ? new Date(c.createdAt).toLocaleDateString()
                      : <span className="subline">not recorded</span>}
                  </td>
                  <td>
                    <Link className="btn btn--sm" to={`/companies/${encodeURIComponent(c.slug)}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
